import { HttpClient, HttpErrorResponse } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { firstValueFrom, timeout } from 'rxjs'
import { CompanyInfoPatch, CompanyRegisterLookupError, CompanyRegisterLookupResult } from './company-register.models'

const ARES_API_URL = 'https://ares.gov.cz/ekonomicke-subjekty-v-be/rest/ekonomicke-subjekty'
const ARES_REQUEST_TIMEOUT_MS = 30_000

type AresValue = string | number | null | undefined

interface AresRegisteredOffice {
  kodStatu?: AresValue
  kodObce?: AresValue
  kodUlice?: AresValue
  kodCastiObce?: AresValue
  kodAdresnihoMista?: AresValue
  nazevObce?: AresValue
  nazevUlice?: AresValue
  nazevCastiObce?: AresValue
  cisloDoAdresy?: AresValue
  cisloDomovni?: AresValue
  cisloOrientacni?: AresValue
  cisloOrientacniPismeno?: AresValue
  psc?: AresValue
  pscTxt?: AresValue
  textovaAdresa?: AresValue
  standardizaceAdresy?: boolean | null
}

interface AresRegisteredOfficeRecord {
  platnostDo?: AresValue
  platnostOd?: AresValue
  primarniZaznam?: boolean
  sidlo?: AresRegisteredOffice
}

interface AresAdditionalData {
  datovyZdroj?: AresValue
  sidlo?: AresRegisteredOfficeRecord[]
}

interface AresEconomicSubject {
  obchodniJmeno?: AresValue
  dic?: AresValue
  sidlo?: AresRegisteredOffice
  primarniZdroj?: AresValue
  dalsiUdaje?: AresAdditionalData[]
}

interface AresOfficeCandidate {
  source: string
  office: AresRegisteredOffice
}

@Injectable({
  providedIn: 'root',
})
export class AresService {
  constructor(private http: HttpClient) {}

  public async lookupAddress(ico: string | number | null | undefined): Promise<CompanyRegisterLookupResult> {
    const normalizedIco = this.toText(ico)
    if (!/^\d{8}$/.test(normalizedIco)) {
      throw new CompanyRegisterLookupError('invalid-ico')
    }

    try {
      const subject = await firstValueFrom(
        this.http
          .get<AresEconomicSubject>(`${ARES_API_URL}/${encodeURIComponent(normalizedIco)}`)
          .pipe(timeout({ first: ARES_REQUEST_TIMEOUT_MS }))
      )
      return {
        values: this.mapAddress(subject),
        status: 'unknown',
      }
    } catch (error) {
      if (error instanceof CompanyRegisterLookupError) {
        throw error
      }
      if (error instanceof HttpErrorResponse) {
        if (error.status === 400) {
          throw new CompanyRegisterLookupError('invalid-ico')
        }
        if (error.status === 404) {
          throw new CompanyRegisterLookupError('not-found')
        }
      }
      throw new CompanyRegisterLookupError('unavailable')
    }
  }

  private mapAddress(subject: AresEconomicSubject): CompanyInfoPatch {
    const registeredOffice = this.getRegisteredOffice(subject)
    const patch: CompanyInfoPatch = {}

    this.setIfPresent(patch, 'companyName', subject.obchodniJmeno)
    this.setIfPresent(patch, 'dic', subject.dic)
    this.setIfPresent(patch, 'country', registeredOffice.kodStatu)
    this.setIfPresent(patch, 'city', registeredOffice.nazevObce)
    this.setIfPresent(patch, 'zip', this.getPostalCode(registeredOffice))

    const street = this.formatStreet(registeredOffice)
    if (street) {
      patch.street = street
    }

    return patch
  }

  private getRegisteredOffice(subject: AresEconomicSubject): AresRegisteredOffice {
    const primaryOffice = subject.sidlo || {}
    const fallbackOffice = this.getBestFallbackOffice(subject)
    return fallbackOffice ? this.mergeOffices(primaryOffice, fallbackOffice) : primaryOffice
  }

  private mergeOffices(
    primaryOffice: AresRegisteredOffice,
    fallbackOffice: AresRegisteredOffice
  ): AresRegisteredOffice {
    return {
      kodStatu: this.preferValue(primaryOffice.kodStatu, fallbackOffice.kodStatu),
      kodObce: this.preferValue(primaryOffice.kodObce, fallbackOffice.kodObce),
      kodUlice: this.preferValue(primaryOffice.kodUlice, fallbackOffice.kodUlice),
      kodCastiObce: this.preferValue(primaryOffice.kodCastiObce, fallbackOffice.kodCastiObce),
      kodAdresnihoMista: this.preferValue(primaryOffice.kodAdresnihoMista, fallbackOffice.kodAdresnihoMista),
      nazevObce: this.preferValue(primaryOffice.nazevObce, fallbackOffice.nazevObce),
      nazevUlice: this.preferValue(primaryOffice.nazevUlice, fallbackOffice.nazevUlice),
      nazevCastiObce: this.preferValue(primaryOffice.nazevCastiObce, fallbackOffice.nazevCastiObce),
      cisloDoAdresy: this.preferValue(this.getAddressNumber(primaryOffice), this.getAddressNumber(fallbackOffice)),
      pscTxt: this.preferValue(this.getPostalCode(primaryOffice), this.getPostalCode(fallbackOffice)),
      textovaAdresa: this.preferValue(primaryOffice.textovaAdresa, fallbackOffice.textovaAdresa),
      standardizaceAdresy: primaryOffice.standardizaceAdresy ?? fallbackOffice.standardizaceAdresy,
    }
  }

  private getBestFallbackOffice(subject: AresEconomicSubject): AresRegisteredOffice | null {
    const primaryOffice = subject.sidlo || {}
    const primarySource = this.toText(subject.primarniZdroj)
    const candidates: AresOfficeCandidate[] = []

    for (const additionalData of subject.dalsiUdaje || []) {
      for (const addressRecord of additionalData.sidlo || []) {
        if (!this.isCurrentPrimaryRecord(addressRecord) || !addressRecord.sidlo) {
          continue
        }

        const office = addressRecord.sidlo
        const source = this.toText(additionalData.datovyZdroj)
        const comesFromPrimarySource = !!primarySource && source === primarySource
        const isSameOffice = this.hasSameOfficeEvidence(primaryOffice, office)
        if (
          this.hasConflictingSharedAddressValue(primaryOffice, office) ||
          (!comesFromPrimarySource && !isSameOffice) ||
          this.getOfficeCompletenessScore(this.mergeOffices(primaryOffice, office)) <=
            this.getOfficeCompletenessScore(primaryOffice)
        ) {
          continue
        }

        candidates.push({ source, office })
      }
    }

    const primarySourceCandidates = primarySource
      ? candidates.filter(candidate => candidate.source === primarySource)
      : []
    const preferredCandidates = primarySourceCandidates.length ? primarySourceCandidates : candidates
    const bestScore = Math.max(
      ...preferredCandidates.map(candidate =>
        this.getOfficeCompletenessScore(this.mergeOffices(primaryOffice, candidate.office))
      ),
      -1
    )
    const bestCandidates = preferredCandidates.filter(
      candidate => this.getOfficeCompletenessScore(this.mergeOffices(primaryOffice, candidate.office)) === bestScore
    )

    if (bestCandidates.length <= 1) {
      return bestCandidates[0]?.office || null
    }

    const signatures = new Set(
      bestCandidates.map(candidate => this.getOfficeSignature(this.mergeOffices(primaryOffice, candidate.office)))
    )
    return signatures.size === 1 ? bestCandidates[0].office : null
  }

  private getOfficeCompletenessScore(office: AresRegisteredOffice): number {
    return [
      office.kodStatu,
      office.nazevObce,
      office.nazevUlice || office.nazevCastiObce,
      this.getAddressNumber(office),
      this.getPostalCode(office),
    ].filter(value => !!this.toText(value)).length
  }

  private isCurrentPrimaryRecord(record: AresRegisteredOfficeRecord): boolean {
    if (record.primarniZaznam !== true) {
      return false
    }

    const today = this.getLocalDate()
    const validFrom = this.toText(record.platnostOd).slice(0, 10)
    const validTo = this.toText(record.platnostDo).slice(0, 10)
    return (!validFrom || validFrom <= today) && (!validTo || validTo >= today)
  }

  private hasSameOfficeEvidence(primaryOffice: AresRegisteredOffice, candidateOffice: AresRegisteredOffice): boolean {
    if (
      this.valuesMatch(primaryOffice.kodAdresnihoMista, candidateOffice.kodAdresnihoMista) &&
      this.toText(primaryOffice.kodAdresnihoMista)
    ) {
      return true
    }

    if (
      this.valuesMatch(primaryOffice.kodObce, candidateOffice.kodObce) &&
      this.toText(primaryOffice.kodObce) &&
      this.valuesMatch(primaryOffice.kodUlice, candidateOffice.kodUlice) &&
      this.toText(primaryOffice.kodUlice)
    ) {
      return true
    }

    const primaryText = this.normalizeAddressText(primaryOffice.textovaAdresa)
    return !!primaryText && primaryText === this.normalizeAddressText(candidateOffice.textovaAdresa)
  }

  private hasConflictingSharedAddressValue(
    primaryOffice: AresRegisteredOffice,
    candidateOffice: AresRegisteredOffice
  ): boolean {
    if (
      this.hasConflictingEquivalentValue(
        this.getAddressNumber(primaryOffice),
        this.getAddressNumber(candidateOffice)
      ) ||
      this.hasConflictingEquivalentValue(this.getPostalCode(primaryOffice), this.getPostalCode(candidateOffice))
    ) {
      return true
    }

    const comparableKeys = [
      'kodStatu',
      'kodObce',
      'kodUlice',
      'kodCastiObce',
      'kodAdresnihoMista',
      'nazevObce',
      'nazevUlice',
      'nazevCastiObce',
    ] as const

    return comparableKeys.some(key => {
      const primaryValue = this.toText(primaryOffice[key])
      const candidateValue = this.toText(candidateOffice[key])
      return !!primaryValue && !!candidateValue && !this.valuesMatch(primaryValue, candidateValue)
    })
  }

  private getOfficeSignature(office: AresRegisteredOffice): string {
    return [
      office.kodStatu,
      office.nazevObce,
      this.toText(office.nazevUlice) || office.nazevCastiObce,
      this.formatStreet(office),
      this.getPostalCode(office),
    ]
      .map(value => this.normalizeAddressText(value))
      .join('|')
  }

  private valuesMatch(first: AresValue, second: AresValue): boolean {
    const normalizedFirst = this.normalizeAddressText(first)
    return !!normalizedFirst && normalizedFirst === this.normalizeAddressText(second)
  }

  private formatStreet(registeredOffice: AresRegisteredOffice): string {
    const streetName = this.toText(registeredOffice.nazevUlice) || this.toText(registeredOffice.nazevCastiObce)
    const addressNumber = this.getAddressNumber(registeredOffice)

    return streetName ? [streetName, addressNumber].filter(Boolean).join(' ') : ''
  }

  private getAddressNumber(registeredOffice: AresRegisteredOffice): AresValue {
    return this.toText(registeredOffice.cisloDoAdresy) || this.formatAddressNumber(registeredOffice)
  }

  private getPostalCode(registeredOffice: AresRegisteredOffice): AresValue {
    return this.toText(registeredOffice.pscTxt) || registeredOffice.psc
  }

  private hasConflictingEquivalentValue(first: AresValue, second: AresValue): boolean {
    const normalizedFirst = this.normalizeEquivalentValue(first)
    const normalizedSecond = this.normalizeEquivalentValue(second)
    return !!normalizedFirst && !!normalizedSecond && normalizedFirst !== normalizedSecond
  }

  private formatAddressNumber(registeredOffice: AresRegisteredOffice): string {
    const houseNumber = this.toText(registeredOffice.cisloDomovni)
    const orientationNumber = this.toText(registeredOffice.cisloOrientacni)
    const orientationLetter = this.toText(registeredOffice.cisloOrientacniPismeno)
    const orientation = `${orientationNumber}${orientationLetter}`

    if (!houseNumber) {
      return orientation
    }

    return orientation ? `${houseNumber}/${orientation}` : houseNumber
  }

  private setIfPresent(patch: CompanyInfoPatch, key: keyof CompanyInfoPatch, value: AresValue): void {
    const text = this.toText(value)
    if (text) {
      patch[key] = text
    }
  }

  private preferValue(primaryValue: AresValue, fallbackValue: AresValue): AresValue {
    return this.toText(primaryValue) ? primaryValue : fallbackValue
  }

  private normalizeAddressText(value: AresValue): string {
    return this.toText(value).normalize('NFC').toLocaleLowerCase('cs-CZ').replace(/\s+/g, ' ')
  }

  private normalizeEquivalentValue(value: AresValue): string {
    return this.normalizeAddressText(value).replace(/\s+/g, '')
  }

  private getLocalDate(): string {
    const date = new Date()
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  private toText(value: AresValue): string {
    return value === null || value === undefined ? '' : String(value).trim()
  }
}
